import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { submitToWeb3Forms } from '../../../utils/web3formsSubmit';
import { trackLeadFormSubmit, trackWhatsAppClick } from '../../../utils/gtm';
import { IconArrowRight, IconWhatsApp } from './Icons';

export const DEFAULT_SERVICE = 'Bookkeeping (Fixed quote)';

const LeadFormLuxury = ({
  formId = 'heroLeadForm',
  isModal = false,
  onCloseModal,
  service = DEFAULT_SERVICE,
}) => {
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name: '',
      phone: '',
    },
  });

  const onSubmit = async (data) => {
    setSubmitError(null);

    let cleanPhone = data.phone.trim();
    if (!cleanPhone.startsWith('+')) {
      cleanPhone = `+971 ${cleanPhone.replace(/^0+/, '')}`;
    }

    const submissionData = {
      name: data.name.trim(),
      phone: cleanPhone,
      service,
    };

    const formName = isModal ? 'standard-auditing-modal' : 'standard-auditing-hero';
    const result = await submitToWeb3Forms(submissionData, {
      form_name: formName,
      subject: 'New Lead - Bookkeeping Fixed Quote (Standard Auditing)',
    });

    if (result.success) {
      trackLeadFormSubmit(formName);
      setIsSuccess(true);
      if (onCloseModal) onCloseModal();
      navigate('/thank-you');
    } else {
      setSubmitError(
        result.message || "We couldn't submit your request right now. Please try again or contact us directly on WhatsApp."
      );
    }
  };

  if (isSuccess) {
    return (
      <div className="bk-form-success" role="status">
        <div className="bk-form-success-icon" aria-hidden="true">✓</div>
        <h3>Thank you. Your request has been received.</h3>
        <p>A named accountant will reply on WhatsApp shortly.</p>
      </div>
    );
  }

  return (
    <form
      id={formId}
      onSubmit={handleSubmit(onSubmit)}
      className="bk-lead-form gtm-lead-form"
      data-form-name={isModal ? 'standard-auditing-modal' : 'standard-auditing-hero'}
      data-form-type="lead-capture"
      noValidate
    >
      {/* Name Field */}
      <div className="bk-field">
        <label htmlFor={`${formId}-name`} className="bk-field-label">
          Your name
        </label>
        <div className={`bk-input-wrap ${errors.name ? 'has-error' : ''}`}>
          <input
            id={`${formId}-name`}
            type="text"
            autoComplete="name"
            placeholder="e.g. Ahmed Al Mansoori"
            className="bk-input"
            aria-invalid={errors.name ? 'true' : 'false'}
            {...register('name', { required: 'Please enter your full name' })}
          />
        </div>
        {errors.name && (
          <span className="bk-field-error">{errors.name.message}</span>
        )}
      </div>

      {/* WhatsApp / Mobile Number Field */}
      <div className="bk-field">
        <label htmlFor={`${formId}-phone`} className="bk-field-label">
          WhatsApp / mobile number
        </label>
        <div className={`bk-input-wrap ${errors.phone ? 'has-error' : ''}`}>
          <div className="bk-phone-prefix" aria-hidden="true">
            <span>AE +971</span>
          </div>
          <input
            id={`${formId}-phone`}
            type="tel"
            autoComplete="tel"
            placeholder="50 123 4567"
            className="bk-input"
            aria-invalid={errors.phone ? 'true' : 'false'}
            {...register('phone', {
              required: 'Please enter your WhatsApp or mobile number',
              validate: (v) =>
                v.replace(/\D/g, '').length >= 7 || 'Please enter at least 7 digits',
            })}
          />
        </div>
        {errors.phone && (
          <span className="bk-field-error">{errors.phone.message}</span>
        )}
      </div>

      {submitError && (
        <div role="alert" className="bk-form-alert">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 2 1 21h22L12 2Zm1 14h-2v2h2v-2Zm0-7h-2v5h2V9Z" />
          </svg>
          <span>{submitError}</span>
        </div>
      )}

      {/* Primary Submit Button */}
      <button
        type="submit"
        disabled={isSubmitting}
        className="bk-btn bk-btn--orange bk-btn--block gtm-submit-btn"
        id={`${formId}-submit`}
        data-gtm-id="lead-form-submit"
        data-form-name={isModal ? 'standard-auditing-modal' : 'standard-auditing-hero'}
      >
        {isSubmitting ? (
          'Submitting...'
        ) : (
          <>
            <span>Get my fixed quote</span>
            <IconArrowRight />
          </>
        )}
      </button>

      {/* Confidentiality Notice */}
      <p className="bk-form-note">
        <strong>100% confidential.</strong> No spam, no sharing your data. We only contact you about your quote.
      </p>

      {/* OR Divider */}
      <div className="bk-or-divider" aria-hidden="true">
        <span>OR</span>
      </div>

      {/* Secondary WhatsApp Button */}
      <a
        href="https://wa.me/971542119784"
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackWhatsAppClick(isModal ? 'modal_lead_card_whatsapp' : 'hero_lead_card_whatsapp')}
        className="bk-btn bk-btn--wa-outline bk-btn--block gtm-whatsapp-btn"
        id={`${formId}-whatsapp`}
        data-gtm-id="whatsapp-cta"
        data-source={isModal ? 'modal_lead_card' : 'hero_lead_card'}
      >
        <IconWhatsApp size={18} />
        <span>Chat on WhatsApp instead</span>
      </a>
    </form>
  );
};

export default LeadFormLuxury;
